import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { login, logout, register } from '@/api/auth';
import { resolveAuthRedirectTarget } from '@/lib/auth-redirect';
import { setNonce } from '@/api/client';
import { buildSiteHref } from '@/lib/site-paths';
import { authKeys } from '@/lib/query-keys';
import { useAuthStore } from '@/stores/auth-store';
import type { LoginRequest, RegisterRequest } from '@/types';

export function useLogin(redirectHref?: string) {
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginRequest) => login(data),
    onSuccess: ({ nonce, user }) => {
      if (nonce) {
        setNonce(nonce);
      }
      setUser(user);
      queryClient.setQueryData(authKeys.session, { nonce, user, success: true });
      void navigate({ href: resolveAuthRedirectTarget(redirectHref) });
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterRequest) => register(data),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logoutStore = useAuthStore((s) => s.logout);

  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      logoutStore();
      setNonce('');
      queryClient.clear();

      if (typeof window !== 'undefined') {
        window.location.replace(buildSiteHref('/login'));
        return;
      }

      void navigate({ to: '/login', replace: true });
    },
  });
}
